import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Users, UserCheck, MessageSquare, AlertTriangle, Search, MoreHorizontal,
  Trash2, Eye, Loader2, ShieldAlert, Megaphone, Flag, CheckCircle2,
} from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { toast } from "sonner";
import { PageHeader } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const input = "rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 py-2.5 text-sm text-[#12142b] outline-none focus:border-[#2340b5]";
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-CA", { month: "short", day: "2-digit", year: "numeric" });
};
const ago = (d) => {
  if (!d) return "";
  const s = (Date.now() - new Date(d).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 172800) return "Yesterday";
  return `${Math.round(s / 86400)}d ago`;
};
const REPORT_ICON = { spam: Megaphone, harassment: ShieldAlert, misleading: Flag };
const STATUS_CHIP = {
  pending: "bg-[#f59e0b]/12 text-[#b45309]",
  under_review: "bg-[#2340b5]/10 text-[#2340b5]",
  resolved: "bg-[#16a34a]/10 text-[#16a34a]",
  dismissed: "bg-[#94a3b8]/15 text-[#475569]",
};

export default function CommunityManagement() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [province, setProvince] = useState("all");
  const [status, setStatus] = useState("all");
  const [viewGroup, setViewGroup] = useState(null);
  const [reviewReport, setReviewReport] = useState(null);
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/community/overview")
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail) || "Could not load community data"); setData({ stats: {}, groups: [], reports: [] }); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const groups = data?.groups || [];
  const provinces = useMemo(() => [...new Set(groups.map((g) => g.province).filter(Boolean))].sort(), [groups]);
  const filtered = groups.filter((g) => {
    const t = q.trim().toLowerCase();
    if (t && ![g.name, g.province].join(" ").toLowerCase().includes(t)) return false;
    if (province !== "all" && g.province !== province) return false;
    if (status !== "all" && g.status !== status) return false;
    return true;
  });
  // overview shows the 6 most active groups (posts + members); "view all" reveals the rest
  const sortedGroups = [...filtered].sort((a, b) => (b.posts + b.members) - (a.posts + a.members));
  const visibleGroups = showAll ? sortedGroups : sortedGroups.slice(0, 6);

  const setReportStatus = async (report, st) => {
    setBusy(true);
    try {
      const { data: res } = await adminApi.patch(`/community/reports/${report.id}`, { status: st });
      toast.success(res.message || "Report updated");
      setReviewReport(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  const removeGroup = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/community/groups/${deleteGroup.id}`);
      toast.success(`${deleteGroup.name} deleted`);
      setDeleteGroup(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    finally { setBusy(false); }
  };

  const stats = data?.stats || {};
  const cards = [
    { icon: Users, label: "Total groups", value: stats.total_groups, testid: "cm-stat-groups" },
    { icon: UserCheck, label: "Active members", value: stats.active_members, testid: "cm-stat-members" },
    { icon: MessageSquare, label: "Total posts", value: stats.total_posts, testid: "cm-stat-posts" },
    { icon: AlertTriangle, label: "Open reports", value: stats.open_reports, testid: "cm-stat-reports", danger: true },
  ];

  return (
    <div data-testid="community-page">
      <PageHeader title="Community Management" subtitle="Manage route groups, community activity, and reports." />
      <div className="space-y-6 p-8">
        {/* stats */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {cards.map((c) => (
            <div key={c.testid} className={`rounded-2xl border p-5 ${c.danger && c.value ? "border-[#e4002b]/20 bg-[#e4002b]/4" : "border-[#12142b]/8 bg-white"}`} data-testid={c.testid}>
              <span className={`grid h-9 w-9 place-items-center rounded-xl ${c.danger ? "bg-[#e4002b]/10" : "bg-[#2340b5]/8"}`}>
                <c.icon className={`h-4.5 w-4.5 ${c.danger ? "text-[#e4002b]" : "text-[#2340b5]"}`} />
              </span>
              <p className="mt-3 text-xs text-[#565b73]">{c.label}</p>
              <p className="font-display text-3xl font-bold text-[#12142b]">{c.value ?? "…"}</p>
            </div>
          ))}
        </div>

        {/* route groups */}
        <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6">
          <h2 className="mb-4 font-display text-lg font-bold text-[#12142b]">Route groups</h2>
          <div className="mb-4 grid gap-3 lg:grid-cols-[1fr_200px_170px]">
            <div className="flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3">
              <Search className="h-4 w-4 text-[#565b73]" />
              <input data-testid="cm-group-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by group, route or province…" className="w-full bg-transparent py-2.5 text-sm outline-none placeholder:text-[#565b73]/60" />
            </div>
            <select data-testid="cm-province-filter" value={province} onChange={(e) => setProvince(e.target.value)} className={input}>
              <option value="all">All provinces</option>
              {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select data-testid="cm-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={input}>
              <option value="all">All statuses</option>
              <option>Active</option>
              <option>Needs review</option>
            </select>
          </div>

          <div className="overflow-x-auto">
            <div className="grid min-w-[820px] grid-cols-[1.6fr_0.9fr_0.6fr_0.5fr_0.6fr_0.8fr_0.8fr_120px] gap-3 border-b border-[#12142b]/10 pb-2 text-xs font-bold uppercase tracking-wide text-[#565b73]">
              <span>Group / Route</span><span>Province</span><span>Members</span><span>Posts</span><span>Reports</span><span>Created</span><span>Status</span><span className="text-right">Actions</span>
            </div>
            {data === null ? (
              <p className="py-10 text-center text-sm text-[#565b73]">Loading groups…</p>
            ) : filtered.length === 0 ? (
              <p className="py-10 text-center text-sm text-[#565b73]" data-testid="cm-groups-empty">No route groups match.</p>
            ) : visibleGroups.map((g) => (
              <div key={g.id} data-testid={`cm-group-row-${g.id}`}
                className="relative grid min-w-[820px] grid-cols-[1.6fr_0.9fr_0.6fr_0.5fr_0.6fr_0.8fr_0.8fr_120px] items-center gap-3 border-b border-[#12142b]/6 py-3.5 last:border-0">
                <span className="truncate text-sm font-semibold text-[#12142b]">{g.name}</span>
                <span className="text-sm text-[#565b73]">{g.province || "—"}</span>
                <span className="text-sm font-semibold text-[#12142b]">{g.members}</span>
                <span className="text-sm font-semibold text-[#12142b]">{g.posts}</span>
                <span className={`text-sm font-semibold ${g.reports ? "text-[#e4002b]" : "text-[#12142b]"}`}>{g.reports}</span>
                <span className="text-sm text-[#565b73]">{fmtDate(g.created_at)}</span>
                <span>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${g.status === "Active" ? "bg-[#16a34a]/10 text-[#16a34a]" : "bg-[#f59e0b]/12 text-[#b45309]"}`} data-testid={`cm-group-status-${g.id}`}>{g.status}</span>
                </span>
                <span className="flex items-center justify-end gap-1">
                  <button data-testid={`cm-group-view-${g.id}`} onClick={() => setViewGroup(g)}
                    className="rounded-lg border border-[#2340b5]/30 px-3.5 py-1.5 text-xs font-bold text-[#2340b5] hover:bg-[#2340b5]/6">View</button>
                  <button data-testid={`cm-group-menu-${g.id}`} onClick={() => setMenuId(menuId === g.id ? null : g.id)}
                    className="rounded-lg p-1.5 text-[#565b73] hover:bg-[#12142b]/6"><MoreHorizontal className="h-4 w-4" /></button>
                  {menuId === g.id && (
                    <span className="absolute right-2 top-12 z-20 w-40 rounded-xl border border-[#12142b]/10 bg-white p-1.5 shadow-xl">
                      <button data-testid={`cm-group-delete-${g.id}`} onClick={() => { setMenuId(null); setDeleteGroup(g); }}
                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-[#e4002b] hover:bg-[#e4002b]/8">
                        <Trash2 className="h-3.5 w-3.5" /> Delete group
                      </button>
                    </span>
                  )}
                </span>
              </div>
            ))}
            {sortedGroups.length > 6 && (
              <div className="pt-4 text-center">
                <button
                  data-testid="cm-view-all-groups"
                  onClick={() => setShowAll(!showAll)}
                  className="rounded-xl border border-[#2340b5]/30 px-5 py-2 text-sm font-bold text-[#2340b5] hover:bg-[#2340b5]/6"
                >
                  {showAll ? "Show less ↑" : `View all ${sortedGroups.length} groups →`}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* reports */}
        <div className="rounded-2xl border border-[#12142b]/8 bg-white p-6">
          <h2 className="mb-4 font-display text-lg font-bold text-[#12142b]">Group reports</h2>
          {data === null ? null : (data.reports || []).length === 0 ? (
            <p className="py-8 text-center text-sm font-semibold text-[#565b73]" data-testid="cm-reports-empty">
              No reports <CheckCircle2 className="inline h-4 w-4 text-[#16a34a]" />
            </p>
          ) : (data.reports || []).map((r) => {
            const Icon = REPORT_ICON[Object.keys(REPORT_ICON).find((k) => (r.reason || "").toLowerCase().includes(k))] || Flag;
            return (
              <div key={r.id} data-testid={`cm-report-${r.id}`} className="flex items-center gap-4 border-b border-[#12142b]/6 py-4 last:border-0 max-md:flex-wrap">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#2340b5]/8"><Icon className="h-4.5 w-4.5 text-[#2340b5]" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-[#12142b]">{r.reason}</p>
                  <p className="truncate text-xs text-[#565b73]">{r.group_name} · Reported by {r.reporter}</p>
                </div>
                <span className="text-xs text-[#565b73]">{ago(r.created_at)}</span>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold capitalize ${STATUS_CHIP[r.status] || STATUS_CHIP.pending}`}>{r.status.replace("_", " ")}</span>
                <button data-testid={`cm-report-review-${r.id}`} onClick={() => setReviewReport(r)}
                  className="rounded-lg border border-[#2340b5]/30 px-3.5 py-1.5 text-xs font-bold text-[#2340b5] hover:bg-[#2340b5]/6">Review report</button>
              </div>
            );
          })}
        </div>
      </div>

      {/* group view dialog */}
      <Dialog open={!!viewGroup} onOpenChange={(o) => !o && setViewGroup(null)}>
        <DialogContent className="max-w-md" data-testid="cm-group-dialog">
          <DialogHeader><DialogTitle>{viewGroup?.name}</DialogTitle></DialogHeader>
          {viewGroup && (
            <div className="space-y-2 text-sm">
              {[["Province", viewGroup.province || "—"], ["Members", viewGroup.members], ["Posts", viewGroup.posts],
                ["Open reports", viewGroup.reports], ["Status", viewGroup.status], ["Created", fmtDate(viewGroup.created_at)]].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-[#12142b]/6 py-2 last:border-0">
                  <span className="text-[#565b73]">{l}</span><span className="font-semibold text-[#12142b]">{v}</span>
                </div>
              ))}
              {viewGroup.reports > 0 && (
                <p className="rounded-xl bg-[#f59e0b]/10 p-3 text-xs font-semibold text-[#b45309]">
                  {viewGroup.reports} open report{viewGroup.reports === 1 ? "" : "s"} — review them in the Group reports section below.
                </p>
              )}
            </div>
          )}
          <DialogFooter className="mt-2 sm:justify-between">
            <button data-testid="cm-group-dialog-delete" onClick={() => { setDeleteGroup(viewGroup); setViewGroup(null); }}
              className="inline-flex items-center gap-2 rounded-xl border border-[#e4002b]/40 px-4 py-2.5 text-sm font-bold text-[#e4002b] hover:bg-[#e4002b]/6">
              <Trash2 className="h-4 w-4" /> Delete group
            </button>
            <button onClick={() => setViewGroup(null)} className="rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390]">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* report review dialog */}
      <Dialog open={!!reviewReport} onOpenChange={(o) => !o && setReviewReport(null)}>
        <DialogContent className="max-w-md" data-testid="cm-report-dialog">
          <DialogHeader><DialogTitle>Review report</DialogTitle></DialogHeader>
          {reviewReport && (
            <div className="space-y-2 text-sm">
              <p className="font-bold text-[#12142b]">{reviewReport.reason}</p>
              {reviewReport.details && <p className="rounded-xl bg-[#f4f6fb] p-3 text-xs text-[#565b73]">{reviewReport.details}</p>}
              <div className="flex justify-between py-1.5"><span className="text-[#565b73]">Group</span><span className="font-semibold text-[#12142b]">{reviewReport.group_name}</span></div>
              <div className="flex justify-between py-1.5"><span className="text-[#565b73]">Reported by</span><span className="font-semibold text-[#12142b]">{reviewReport.reporter}</span></div>
              <div className="flex justify-between py-1.5"><span className="text-[#565b73]">When</span><span className="font-semibold text-[#12142b]">{ago(reviewReport.created_at)}</span></div>
              <div className="flex justify-between py-1.5"><span className="text-[#565b73]">Status</span><span className="font-semibold capitalize text-[#12142b]">{reviewReport.status.replace("_", " ")}</span></div>
            </div>
          )}
          <DialogFooter className="mt-2 flex-wrap gap-2 sm:justify-between">
            <button data-testid="cm-report-dismiss" onClick={() => setReportStatus(reviewReport, "dismissed")} disabled={busy}
              className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb] disabled:opacity-60">Dismiss</button>
            <div className="flex gap-2">
              <button data-testid="cm-report-under-review" onClick={() => setReportStatus(reviewReport, "under_review")} disabled={busy}
                className="rounded-xl border border-[#2340b5]/40 px-4 py-2.5 text-sm font-bold text-[#2340b5] hover:bg-[#2340b5]/6 disabled:opacity-60">Under review</button>
              <button data-testid="cm-report-resolve" onClick={() => setReportStatus(reviewReport, "resolved")} disabled={busy}
                className="inline-flex items-center gap-2 rounded-xl bg-[#16a34a] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#15803d] disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Resolve
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* delete group confirm */}
      <Dialog open={!!deleteGroup} onOpenChange={(o) => !o && setDeleteGroup(null)}>
        <DialogContent className="max-w-sm" data-testid="cm-delete-dialog">
          <DialogHeader><DialogTitle>Delete {deleteGroup?.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#565b73]">This permanently removes the group and its posts for all members. This cannot be undone.</p>
          <DialogFooter className="mt-3 gap-2 sm:justify-between">
            <button onClick={() => setDeleteGroup(null)} disabled={busy}
              className="rounded-xl border border-[#12142b]/12 px-4 py-2.5 text-sm font-semibold text-[#12142b] hover:bg-[#f4f6fb]">Cancel</button>
            <button data-testid="cm-delete-confirm" onClick={removeGroup} disabled={busy}
              className="inline-flex items-center gap-2 rounded-xl bg-[#e4002b] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#b80023] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete group
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
